import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getSearchResultsContact, getVerfiyNumber } from '../../api';
import { getContactOptions, KeyValuePair } from '../../api/contact-control';
import { HasClassName } from '../../component-utils';
import { toObject } from '../../data-utils';
import { useSelector } from '../../hook-utils';
import { ClidObjectType, Contact, ContactBasic, ContactType } from "../../model";
import { actions } from '../../store';
import { selectMyAccess, selectMyself } from '../../store/reducers/config';
import { getContactSaveStatus } from '../../store/reducers/contacts';
import { selectMedia } from '../../store/reducers/media';
import Autocomplete from '../Autocomplete';
import Button from '../Button';
import DropdownButton from '../DropdownButton';
import FormLabel from '../FormLabel';
import { ReactComponent as AddContactIcon } from '../icons/add.svg';
import { ReactComponent as DropdownIcon } from '../icons/dropdown-icon.svg';
import { ReactComponent as ContactIcon } from '../icons/facebook-user.svg';
import Input, { MultiInput } from '../Input';
import { Menu, MenuItem } from '../Menu';
import './ContactPage.scss';

interface Props {
  mediaId: string
}

export default function ContactPage({ mediaId }: Props) {
  const media = useSelector(s => selectMedia(s, mediaId))
  const [addContact, setAddContact] = useState(false)
  const [contactId, setContactId] = useState(media ? media.clidObjectKey : '')
  const [contact, setContact] = useState<ContactBasic>()
  const [value, setValue] = useState('')

  const clidObjectKey = media && media.clidObjectKey
  const clidObjectType = media && media.clidObjectType
  const mediaType = media && media.mediaType

  useEffect(() => {
    if (!clidObjectKey) return

    setAddContact(false)
    setContact(undefined)
    setContactId(clidObjectKey)
    setValue('')
  }, [clidObjectKey])

  const render = useMemo(() => {
    const matched = clidObjectKey !== '' && (clidObjectType !== ClidObjectType.personalContact && clidObjectType !== ClidObjectType.companyContact && clidObjectType !== ClidObjectType.customerContact)
    const showContactForm = !matched && (contactId !== '' || addContact || contact)

    const handleCancel = () => {
      setAddContact(false)
      setContactId('')
      setContact(undefined)
    }


    return <div className='ContactPage'>
      {!showContactForm && !matched && mediaType !== 'TWEET' && mediaType !== 'FB' && <section className='link'>
        <h2>Link Contact</h2>
        <ContactLookup value={value} onChange={setValue} placeholder='Search Contacts' autoFocus emailRequired={false} localServer={true} includePersonal={true} onSelected={item => {
          setContact(item)
          setContactId(item.id)
          setValue('')
          return ''
        }} />

        {contactId === '' && !addContact && <div title='Create new contact' className='addContact' onClick={() => {
          setAddContact(true)
          setContactId('')
          setContact(undefined)
        }}>
          <AddContactIcon />
          <div>Add contact</div>
        </div>}
      </section>}

      {matched && <div className='matched'>
        <p>Media was matched on {clidObjectTypeDescription(clidObjectType)}[{clidObjectKey}] and cannot be used within the 'History' tab.</p>
        Please talk to your system administrator if think this is an issue.
      </div>}

      {mediaType === 'TWEET' || mediaType === 'FB' &&
        <div className='matched'>
          <p>Contacts cannot be matched to this media type.</p>
        </div>
      }

      {showContactForm && <ContactForm mediaId={mediaId} contactId={contactId} contactType={ContactType.Customer} search={contact} onCancel={handleCancel} />}
    </div>
  }, [addContact, clidObjectKey, clidObjectType, contact, contactId, mediaId, mediaType, value])

  if (!media) return null


  return render
}

function clidObjectTypeDescription(clidObjectType?: ClidObjectType) {
  switch (clidObjectType) {
    case ClidObjectType.extension:
      return 'Extension'
    case ClidObjectType.queue:
      return 'Queue'
    case ClidObjectType.speedDial:
      return 'Speed dial'
    default:
      return clidObjectType
  }
}

const PhoneProps: StringProps<Contact>[] = ['Business', 'Home', 'Mobile', 'Other']
const LinkingProps: StringProps<Contact>[] = [...PhoneProps, 'CustomId', 'Email']

export function ContactForm({ mediaId, contactId, contactType, server, search, onCancel, description }: { mediaId?: string, contactId: string, contactType: ContactType, server?: string, search?: ContactBasic, onCancel?(): void, description?: string }) {
  const dispatch = useDispatch()
  const media = useSelector(s => mediaId !== undefined && selectMedia(s, mediaId))
  const [contact, setContact] = useState<Contact>()
  const [oldContact, setOldContact] = useState<Contact>()
  const [type, setType] = useState<Record<string, string>>()
  const [priority, setPriority] = useState<Record<string, string>>()
  const [queue, setQueue] = useState<Record<string, string>>()
  const [agent, setAgent] = useState<Record<string, string>>()
  const [region, setRegion] = useState<Record<string, string>>()
  const myId = useSelector(selectMyself)
  const [showMenu, setShowMenu] = useState(false)
  const [showTypeMenu, setShowTypeMenu] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuTypeButtonRef = useRef<HTMLDivElement>(null)
  const [unlinked, setUnlinked] = useState(false) //linked with current media
  const [save, setSave] = useState(false)
  const status = useSelector(getContactSaveStatus)
  const access = useSelector(selectMyAccess)

  useEffect(() => {
    let mounted = true
    load()
    return () => { mounted = false }

    async function load() {
      if (!myId) return

      let data = null
      if (search) {
        data = await getContactOptions(myId, search.id, contactType, server)
      } else {
        data = await getContactOptions(myId, contactId, contactType, server)
      }

      if (!mounted) { return }

      if (!data.contact) {
        dispatch(actions.showClientNotification('Contact couldn\'t be retrieved from the server'))
      }

      //setup default values for added
      if (data.contact) {
        if (data.contact.ContactId === 0) {
          if (contactType === ContactType.Personal) {
            data.contact = { ...data.contact, ExtensionNumber: myId.id, ServerName: myId.server, Description: description === undefined ? '' : description }
          } else if (contactType === ContactType.Company) {
            data.contact = { ...data.contact, ExtensionNumber: "0", ServerName: myId.server, Description: description === undefined ? '' : description }
          } else if (contactType === ContactType.Customer) {
            data.contact = { ...data.contact, ExtensionNumber: "-1", ServerName: myId.server, Description: description === undefined ? '' : description }
          }
        }
      }

      setContact(data.contact)
      setOldContact(data.contact)

      setType(getObject(data.type))
      setPriority(getObject(data.priority))
      setQueue(getObject(data.queue))
      setAgent(getObject(data.agent))
      setRegion(toObject(data.region, r => r.id, r => r.name))
    }
  }, [contactId, dispatch, myId, search, server, contactType, description])

  const getCopyValueFor = useCallback(<K extends StringProps<Contact>>(prop: K) => {
    if (!contact || !media) { return null }

    if (prop === 'Description') {
      if (media.mediaType === 'TC') {
        if (media.clidDescription === contact.Description) {
          return null
        } else {
          return media.clidDescription
        }
      } else {
        return null
      }
    } else if (prop === 'CustomId') {
      if (media.mediaType === 'TC') {
        if (media.clidAccountCode === contact.CustomId) {
          return null
        } else {
          return media.clidAccountCode
        }
      } else {
        return null
      }
    } else if (prop === 'Email') {
      if (media.mediaType === 'TC') {
        if (media.extensionEmail === contact.Email) {
          return null
        } else {
          return media.extensionEmail ? media.extensionEmail.replace(';', '') : '';  // jo report pluse having ; on the end so just triming if needed.  I can't replicate
        }
      } else if (media.mediaType !== 'EMAIL' || media.clid === contact.Email) {
        return null
      }
    } else if (((media.mediaType === 'CALL' || media.mediaType === 'VM') && media.clid === contact[prop]) ||
      (media.mediaType === 'TC' && media.clid.indexOf('@') === -1 && media.clid === contact[prop]) || //tc was matched on email if @ in clid
      (media.mediaType === 'TC' && media.clid.indexOf('@') > 0) ||
      (media.mediaType !== 'CALL' && media.mediaType !== 'VM' && media.mediaType !== 'TC')) {
      //this is number properties
      return null
    }

    return media.clid
  }, [contact, media])

  useEffect(() => {
    const props: StringProps<Contact>[] =
      !media ? [] :
        media.mediaType === 'CALL' || media.mediaType === 'VM' ? PhoneProps :
          media.mediaType === 'EMAIL' ? ['Email'] :
            media.mediaType === 'TC' ? ['Email', 'CustomId'] :
              []

    setUnlinked(props.map(getCopyValueFor).every(x => x !== null))
  }, [getCopyValueFor, media])

  useEffect(() => {
    setSave(JSON.stringify(contact) !== JSON.stringify(oldContact))
  }, [contact, oldContact])

  useEffect(() => {
    //if saved and contact has chanegd
    if (status === 'saved' && contact && contact.ContactId !== 0) {
      setOldContact(contact)
      dispatch(actions.setSelectedContactState('none'))
    }
  }, [status, contact, dispatch])


  if (!myId || contact === undefined || !type || !priority || !queue || !agent || !region || !access) {
    return <>Loading...</>
  } else if (contact === null) {
    return <>System Error</>
  }

  const onBlur = (prop: StringProps<Contact>) => async () => {
    const c = contact
    const number = await getVerfiyNumber(contact[prop], contact ? contact.RegionId : '')
    if (c === contact) {
      setContact({ ...contact, [prop]: number })
    }
  }

  const handleCancel = () => {
    setContact(oldContact)
  }

  const handleDelete = () => {
    dispatch(actions.requestDeleteContact(!media ? '' : media.id, { id: contact.ContactId.toString(), server: contact.ServerName }, contact.Description))
  }

  const handleTypeChange = (contactType: ContactType) => {
    const number = contactType === ContactType.Personal ? myId.id : contactType === ContactType.Customer ? '-1' : '0'

    setContact({
      ...contact,
      ExtensionNumber: number,
    })
  }

  async function handleSave() {
    if (!contact || !oldContact || status !== 'none') { return }

    //make sure we format all numbers
    await Promise.all(PhoneProps.map(async prop => {
      setContact({ ...contact, [prop]: await getVerfiyNumber(contact[prop], contact ? contact.RegionId : '') })
    }))

    setContact({
      ...contact,
      Description: contact.Description.replace(/®/g, '(R)'),
      Notes: contact.Notes.replace(/®/g, '(R)'),
      Company: contact.Company.replace(/®/g, '(R)'),
    })

    const propsToCompare: Array<keyof Contact> = ['Business', 'Home', 'Mobile', 'Email']
    const reload = propsToCompare.some(prop => contact[prop] !== oldContact[prop])

    setContact({
      ...contact,
      Description: contact.Description.replace(/®/g, '(R)'),
      Notes: contact.Notes.replace(/®/g, '(R)'),
      Company: contact.Company.replace(/®/g, '(R)'),
    })

    dispatch(actions.updateContact(!media ? '' : media.id, contact, reload))
  }

  const e = contact.ContactId !== 0 && parseInt(contact.ExtensionNumber)
  const canCancel = contact.ContactId === 0 || window.location.pathname.indexOf('/directory') !== -1 || media
  return <div className={classNames('ContactForm', unlinked && 'unlinked')}>
    <h2>
      Contact Details
      <Button primary={!(media && media.clidObjectKey !== contact.ContactId.toString() && contact.ContactId > 0 && !unlinked)} onClick={handleSave} disabled={(!save && media && media.clidObjectKey === contact.ContactId.toString()) || !contact.Description || status === 'saving'}>Save</Button>
      {(media && media.clidObjectKey !== contact.ContactId.toString() && contact.ContactId > 0 && !unlinked) && <Button primary onClick={handleSave} disabled={!contact.Description || status === 'saving'}>Link</Button>}
      <Button innerRef={menuButtonRef} onClick={() => setShowMenu(true)}>...</Button>
      {canCancel && <Button onClick={onCancel}>X</Button>}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} target={menuButtonRef.current} placement='bottom-end'>
        {canCancel && <MenuItem onClick={onCancel}>Cancel</MenuItem>}
        {contact.ContactId !== 0 && <MenuItem disabled={!save} onClick={handleCancel}>Revert Contact</MenuItem>}
        {contact.ContactId !== 0 && <MenuItem disabled={e < 1 && !access.features.directoryCompanyContactDelete || e > 0 && !access.features.directoryPersonalContactDelete} onClick={handleDelete}>Delete Contact</MenuItem>}
      </Menu>
    </h2>
    <div className='form'>
      <div className='top'>
        <div className='type'>
          {getContactIcon(contact.ExtensionNumber)}
          <div className='text' onClick={() => setShowTypeMenu(true)}>
            <div>{getContactTypeString(contact.ExtensionNumber)}</div>
            <div ref={menuTypeButtonRef} ><DropdownIcon /></div>
          </div>
          <Menu open={showTypeMenu} onClose={() => setShowTypeMenu(false)} target={menuTypeButtonRef.current} placement='bottom-end'>
            {(access.features.directoryCompanyContactAdd && contact.ContactId === 0 || access.features.directoryCompanyContactEdit && contact.ContactId !== 0) && <MenuItem onClick={() => handleTypeChange(ContactType.Company)} >Company</MenuItem>}
            {(access.features.directoryCompanyContactAdd && contact.ContactId === 0 || access.features.directoryCompanyContactEdit && contact.ContactId !== 0) && <MenuItem onClick={() => handleTypeChange(ContactType.Customer)}>Customer</MenuItem>}
            {(access.features.directoryPersonalContactAdd && contact.ContactId === 0 || access.features.directoryPersonalContactEdit && contact.ContactId !== 0) && <MenuItem onClick={() => handleTypeChange(ContactType.Personal)}>Personal</MenuItem>}
          </Menu>
        </div>
        {renderInputWithLabel('Description', <>Name<span className='required'>*</span> {renderCopyButton('Description')}</>)}
      </div>
      <div className='column'>
        {renderInputWithLabel('CustomId', <>Account number</>)}
        {renderDropdownWithLabel('Priority', 'Priority', priority)}
      </div>
      <div className='column'>
        {renderInputWithLabel('Business', 'Business Phone', false, { key: 'Business', onBlur: onBlur('Business') })}
        {renderInputWithLabel('Home', 'Home phone', false, { key: 'Home', onBlur: onBlur('Home') })}
        {renderInputWithLabel('Mobile', 'Mobile', false, { key: 'Mobile', onBlur: onBlur('Mobile') })}
        {renderInputWithLabel('Other', 'Other', false, { key: 'Other', onBlur: onBlur('Other') })}
        {renderInputWithLabel('Email', 'Email')}
      </div>
      <div className='column'>
        {renderInputWithLabel('Company', 'Company')}
        {renderInputWithLabel('Department', 'Department')}
        {renderInputWithLabel('JobTitle', 'Position')}
        {renderDropdownWithLabel('RegionId', 'Region', region)}
      </div>
      <div className='column'>
        {renderDropdownWithLabel('Queue', 'Preferred queue', queue)}
        <FormLabel label='Preferred agent group'>
          <DropdownButton setWidth items={Object.entries(agent).map(([key, value]) => <MenuItem key={key} onClick={() => setContact({
            ...contact,
            PagId: key ? parseInt(key) : undefined,
          })}>{value}</MenuItem>)}>
            {agent[contact.PagId || '']}
          </DropdownButton>
        </FormLabel>
      </div>
      {renderInputWithLabel('Notes', 'Notes', true)}
    </div>
  </div >

  function renderInput<K extends StringProps<Contact>>(prop: K, multi: boolean = false, props: Partial<React.Attributes & React.HTMLAttributes<HTMLElement>> = {}) {
    if (!contact || !myId) { return null }
    const Component = multi ? MultiInput : Input

    return <Component {...props} value={contact[prop]}
      onChange={(e: React.ChangeEvent<{ value: string }>) => setContact({ ...contact, [prop]: e.currentTarget.value })} />
  }

  function renderInputWithLabel<K extends StringProps<Contact>>(prop: K, label: NonNullable<React.ReactNode>, multi: boolean = false, { key, ...props }: Partial<React.Attributes & React.HTMLAttributes<HTMLElement>> = {}) {
    const copyButton = LinkingProps.includes(prop) ? renderCopyButton(prop) : null
    return <FormLabel label={<>{label}{copyButton}</>} key={key}>
      {renderInput(prop, multi, props)}
    </FormLabel>
  }

  function renderDropdown<K extends StringProps<Contact>>(prop: K, items: Record<string, string>) {
    if (!contact) { return null }

    return <DropdownButton setWidth items={Object.entries(items).map(([key, value]) => <MenuItem key={key} onClick={() => setContact({
      ...contact,
      [prop]: key,
    })}>{value}</MenuItem>)}>
      {items[contact[prop]]}
    </DropdownButton>
  }

  function renderDropdownWithLabel<K extends StringProps<Contact>>(prop: K, label: string, items: Record<string, string>) {
    return <FormLabel label={label}>
      {renderDropdown(prop, items)}
    </FormLabel>
  }

  function renderCopyButton<K extends StringProps<Contact>>(prop: K) {
    if (!contact) { return }

    const value = getCopyValueFor(prop)
    if (value === null) {
      return null
    }

    return <button className='copyButton' onClick={() => {
      setContact(c => c && {
        ...c,
        [prop]: value,
      })
    }}>Set to {value}</button>
  }
}

type StringProps<T> = {
  [P in keyof T]-?: T[P] extends string ? P : never;
}[keyof T]

function getObject(pairs: KeyValuePair[]): Record<string, string> {
  return toObject(pairs, x => x.Key, x => x.Value)
}

export function getContactIcon(extensionNumber?: string) {
  const className = extensionNumber === undefined || extensionNumber === '' ? '' : extensionNumber === '-1' ? 'contact-customer-svg' : extensionNumber === '0' ? 'contact-company-svg' : 'contact-personal-svg'
  return <ContactIcon className={className} />
}

export function getContactTypeString(extensionNumber?: string) {
  return extensionNumber === undefined || extensionNumber === '' ? '' : extensionNumber === '-1' ? 'Customer' : extensionNumber === '0' ? 'Company' : 'Personal'
}

export function getContactType(extensionNumber?: string) {
  return extensionNumber === undefined || extensionNumber === '' ? ContactType.None : extensionNumber === '-1' ? ContactType.Customer : extensionNumber === '0' ? ContactType.Company : ContactType.Personal
}

type ContactLookupProps = HasClassName & {
  enabled?: boolean
  autoFocus?: boolean
  placeholder?: string
  value: string
  emailRequired: boolean
  localServer: boolean
  includePersonal: boolean
  onChange: (value: string) => void
  onSelected: (item: ContactBasic) => string
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
}

export function ContactLookup({ className, autoFocus, onSelected, placeholder, value, emailRequired, onChange, onKeyUp, onKeyDown, localServer, includePersonal, enabled }: ContactLookupProps) {
  const myId = useSelector(selectMyself)
  const ref = useRef<string>('')

  async function searchContacts(input: string) {
    if (!input || !myId || ref.current === input || enabled === false) { return [] }
    return await getSearchResultsContact(myId, input, emailRequired, localServer, includePersonal)
  }

  return <Autocomplete className={className} value={value} onKeyUp={onKeyUp} onKeyDown={onKeyDown} onChange={e => onChange(e.currentTarget.value)} placeholder={placeholder} getItems={searchContacts} autoFocus={autoFocus}
    renderItem={item => <><ContactIcon /> {item.description}</>} onSelected={(item) => {
      if (onSelected) {
        const x = onSelected(item)
        ref.current = x
      }
    }} />
}
